import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import MarketingNav from "@/components/layout/MarketingNav";
import MarketingFooter from "@/components/layout/MarketingFooter";
import { api } from "@/lib/api";
import { MARKETING } from "@/constants/testIds";

const TYPES = {
  demo: { title: "Request a Personalized Demo", sub: "See ATLAS™ in action tailored to your program." },
  discovery: { title: "Schedule a Discovery Call", sub: "A 30-minute conversation about your entrepreneurship program." },
  pricing: { title: "Request Pricing", sub: "Custom quotes for organizations, universities, and government programs." },
};

export default function RequestDemo() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const type = new URLSearchParams(search).get("type") || "demo";
  const meta = TYPES[type] || TYPES.demo;
  const [form, setForm] = useState({ name: "", email: "", organization: "", role: "", org_size: "", message: "", request_type: type });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads/demo", form);
      toast.success("Request received. Our team will be in touch within one business day.");
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <MarketingNav />
      <section className="border-b border-stone-200 bg-white">
        <div className="mx-auto grid max-w-6xl gap-16 px-6 py-24 lg:grid-cols-2 lg:px-8">
          <div>
            <div className="label-mono mb-4">Get Started</div>
            <h1 className="font-display text-5xl font-bold leading-tight tracking-tight text-slate-950">{meta.title}</h1>
            <p className="mt-6 text-lg leading-relaxed text-stone-600">{meta.sub}</p>
            <div className="mt-10 space-y-4 border-t border-stone-200 pt-8">
              <div className="label-mono !text-[10px]">What Happens Next</div>
              {[
                ["01", "We review your program details"],
                ["02", "A specialist reaches out within 1 business day"],
                ["03", "Personalized walkthrough & pricing proposal"],
                ["04", "Optional pilot for select organizations"],
              ].map(([n, t]) => (
                <div key={n} className="flex gap-4 text-sm text-slate-950">
                  <div className="font-mono text-xs text-emerald-600">{n}</div>
                  <div>{t}</div>
                </div>
              ))}
            </div>
          </div>
          <form onSubmit={submit} className="card-flat">
            <div className="grid gap-4">
              <Field label="Full Name" required>
                <input data-testid={MARKETING.demoFormName} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Work Email" required>
                <input data-testid={MARKETING.demoFormEmail} required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Organization">
                <input data-testid={MARKETING.demoFormOrg} value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className={inputCls} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Your Role">
                  <input data-testid={MARKETING.demoFormRole} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputCls} placeholder="Program Director, EDO Director…" />
                </Field>
                <Field label="Program Size">
                  <select data-testid={MARKETING.demoFormSize} value={form.org_size} onChange={(e) => setForm({ ...form, org_size: e.target.value })} className={inputCls}>
                    <option value="">Select…</option>
                    <option>Individual</option>
                    <option>Up to 50 entrepreneurs</option>
                    <option>50–100</option>
                    <option>100–250</option>
                    <option>250–1,000</option>
                    <option>1,000+</option>
                  </select>
                </Field>
              </div>
              <Field label="What are you hoping to achieve?">
                <textarea data-testid={MARKETING.demoFormMessage} rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} />
              </Field>
              <button data-testid={MARKETING.demoFormSubmit} type="submit" disabled={loading} className="btn-primary mt-2 w-full">
                {loading ? "Sending…" : "Send Request"}
              </button>
              <p className="text-center font-mono text-[10px] uppercase tracking-wider text-stone-400">
                We respect your privacy. No spam, ever.
              </p>
            </div>
          </form>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}

const inputCls = "w-full rounded-md border border-stone-300 bg-white px-3 py-2.5 text-sm text-slate-950 outline-none transition-all focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label-mono block mb-1.5">{label} {required && <span className="text-red-500">*</span>}</span>
      {children}
    </label>
  );
}
